import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';
import { Zap, Moon, Sun, ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <NavigationMenu />
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg">FlipValue</span>
            </button>
          </div>
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>
        <p className="text-muted-foreground mb-6">Last updated: January 12, 2026</p>

        <div className="prose prose-sm dark:prose-invert max-w-none space-y-6">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground">By accessing or using FlipValue ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Description of Service</h2>
            <p className="text-muted-foreground">FlipValue is a valuation tool for resellers that provides market price estimates, profit calculations, inventory management, release tracking, and AI-powered item identification features. Our valuations are estimates based on publicly available market data from eBay and should not be considered as financial advice. Market data is sourced from your selected regional eBay marketplace.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. User Accounts</h2>
            <p className="text-muted-foreground mb-2">To use certain features, you must create an account or sign in via Google OAuth. You agree to:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Not share your account with others or create multiple accounts</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Subscription Plans</h2>
            <p className="text-muted-foreground mb-2">We offer various subscription plans with different features and limits:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li><strong>Free Plan:</strong> 50 lifetime actions (searches + calculations combined), 10 inventory items max, basic sharing, eBay-only calculator</li>
              <li><strong>Basic Plan:</strong> Unlimited searches and calculations, unlimited inventory, 1 calculator platform, advanced sharing</li>
              <li><strong>Pro Plan:</strong> Unlimited everything, all calculator platforms, price alerts, release tracking, CSV exports</li>
              <li><strong>Lifetime Pro:</strong> One-time purchase for permanent Pro access (limited availability)</li>
            </ul>
            <p className="text-muted-foreground mt-2">Paid subscriptions are billed in advance on a monthly or annual basis. Prices are subject to change with notice. Lifetime Pro is a one-time purchase and is non-refundable.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Free Trials</h2>
            <p className="text-muted-foreground">We may offer free trials for paid subscription plans. At the end of the trial period, you will be automatically charged unless you cancel before the trial ends. You may cancel at any time during the trial without charge. Users who sign up using a referral code for a free month will not receive an additional trial period.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Referral Program</h2>
            <p className="text-muted-foreground mb-2">Our referral program allows users to earn rewards by inviting others:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Referrers receive credit only after their referee makes their first paid subscription payment</li>
              <li>Referees receive a free month of service upon signup with a valid referral code</li>
              <li>Referral rewards cannot be combined with other promotions unless explicitly stated</li>
              <li>We reserve the right to modify or terminate the referral program at any time</li>
              <li>Abuse of the referral program may result in account termination</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Regional Marketplace</h2>
            <p className="text-muted-foreground">Our Service provides market data from multiple eBay regional marketplaces (US, UK, Germany, France, Australia, Canada, Italy, Spain). You may select your preferred region in settings. Pricing data and availability may vary by region. We do not guarantee the accuracy or availability of data from any specific marketplace.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. AI-Powered Features</h2>
            <p className="text-muted-foreground">Our Service uses artificial intelligence (AI) for item identification from photos. AI identifications are estimates and may not be accurate. You should verify all AI-generated information before making business decisions. Images uploaded for AI processing are not stored permanently after analysis.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Refund Policy</h2>
            <p className="text-muted-foreground">Subscription fees are generally non-refundable except where required by law. If you cancel your subscription, you will continue to have access until the end of your current billing period. Lifetime Pro purchases are non-refundable. Please see our full <a href="/refund" className="text-primary hover:underline">Refund Policy</a> for details.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Acceptable Use</h2>
            <p className="text-muted-foreground mb-2">You agree not to:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use automated tools to scrape or access the Service</li>
              <li>Share your account credentials with others</li>
              <li>Create multiple accounts to abuse free tiers or referral programs</li>
              <li>Resell or redistribute the Service without permission</li>
              <li>Upload malicious content or attempt to compromise our AI systems</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Intellectual Property</h2>
            <p className="text-muted-foreground">The Service and its original content, features, and functionality are owned by FlipValue and are protected by international copyright, trademark, and other intellectual property laws.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground">THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. We do not guarantee the accuracy of valuations, AI identifications, or market data. You acknowledge that prices and values are estimates only and actual sale prices may vary. Market data availability depends on third-party services and regional availability.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">13. Limitation of Liability</h2>
            <p className="text-muted-foreground">IN NO EVENT SHALL FLIPVALUE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE SERVICE, INCLUDING BUT NOT LIMITED TO FINANCIAL LOSSES BASED ON OUR VALUATIONS OR AI IDENTIFICATIONS.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">14. Changes to Terms</h2>
            <p className="text-muted-foreground">We reserve the right to modify these terms at any time. We will notify users of material changes via email or through the Service. Continued use after changes constitutes acceptance of the new terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">15. Termination</h2>
            <p className="text-muted-foreground">We may terminate or suspend your account and access to the Service immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties. This includes abuse of the referral program or creation of multiple accounts.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">16. Governing Law</h2>
            <p className="text-muted-foreground">These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">17. Contact Us</h2>
            <p className="text-muted-foreground">If you have any questions about these Terms, please contact us at:</p>
            <p className="text-primary mt-2">support@flipvalue.app</p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
